import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import {
  OperatorInfo,
  AreaInfo,
  LayoutInfo,
  getAvailableOperatorInfos,
  getAreaEntry,
  OperatorRole,
  getAllAreaEntries,
  getAllLayoutEntries,
  updateAreaEntry,
  CreateAreaInfo,
  addAreaEntry,
  updateLayoutEntry,
  CreateLayoutInfo,
  addLayoutEntry,
  deleteAreaEntry,
  deleteLayyoutEntry,
  sendOperatorInvitation,
  updateOperatorValues,
  deleteOperator,
  LayoutType,
} from "../Utils/BackendInterface";
import {
  SortInfo,
  sortSubject,
  SortType,
  listSort,
  showSuccessNotification,
  showErrorNotification,
} from "../Utils/utils";
import { CardDropdownInput } from "./CardManagement";
import { GeneralManagementList } from "./GeneralManagementList";
import closeIcon from "../resources/img/closeIcon.svg";

export const DashboardSettingsView = () => {
  const [t, i18n] = useTranslation();
  const outletInfo = useOutletContext<{ currentUser: OperatorInfo }>();
  const [availableOperators, setAvailableOperators] = useState<OperatorInfo[] | undefined>();
  const [showEditAreaView, setShowEditAreaView] = useState<AreaInfo | undefined>(undefined);
  const [showCreateAreaView, setShowCreateAreaView] = useState(false);
  const [showEditLayoutView, setShowEditLayoutView] = useState<LayoutInfo | undefined>(undefined);
  const [showCreateLayoutView, setShowCreateLayoutView] = useState(false);
  const [showInviteOperator, setShowInviteOperator] = useState(false);
  const [showUpdateOperator, setShowUpdateOperator] = useState<OperatorInfo | undefined>();
  const [currentSorting, setCurrentSorting] = useState<SortInfo | undefined>();
  const [availableAreaEntries, setAvailableAreaEntries] = useState<AreaInfo[] | undefined>(
    undefined
  );
  const [availableLayoutEntries, setAvailableLayoutEntries] = useState<LayoutInfo[] | undefined>(
    undefined
  );

  useEffect(() => {
    const sortSub = sortSubject.subscribe((sortInfo) => {
      setCurrentSorting(sortInfo);
    });

    return function cleanup() {
      sortSub?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (currentSorting && currentSorting.type !== SortType.NONE) {
      const toBeSorted = availableOperators?.slice();
      toBeSorted?.sort((a: OperatorInfo, b: OperatorInfo) => {
        return listSort(a, b, currentSorting);
      });
      setAvailableOperators(toBeSorted);
    } else if (currentSorting && currentSorting.type === SortType.NONE) {
      getData();
    }
  }, [currentSorting]);

  useEffect(() => {
    // update the value whenever the overlay closes
    if (!showUpdateOperator || !showInviteOperator) {
      getData();
    }
  }, [showUpdateOperator, showInviteOperator]);

  const getData = () => {
    getAvailableOperatorInfos()
      .then(async (infos) => {
        if (infos) {
          let _areaDisplayNameCache: Map<number, string> = new Map();
          const displayOperatorInfos: OperatorInfo[] = infos as any;

          // add the extra displayoperatorinfos here
          for (const entry of displayOperatorInfos) {
            if (!_areaDisplayNameCache.has(entry.idArea)) {
              _areaDisplayNameCache.set(
                entry.idArea,
                (await getAreaEntry(entry.idArea))?.name ?? ""
              );
            }
            entry.areaDisplayName = _areaDisplayNameCache.get(entry.idArea);
          }
          setAvailableOperators(displayOperatorInfos);
        }
      })
      .then(() => {
        if (outletInfo.currentUser.role === OperatorRole.ADMIN) {
          getAllAreaEntries().then((res) => setAvailableAreaEntries(res));
        }
        getAllLayoutEntries(
          outletInfo.currentUser.role !== OperatorRole.ADMIN
            ? outletInfo.currentUser.idArea
            : undefined
        ).then(async (res) => {
          let _areaDisplayNameCache: Map<number, string> = new Map();
          const displayLayoutInfo: any[] = res as any;

          // add the extra info here. TODO: rework this workaround to show the extra information, that isn't part of the database entry
          for (const entry of displayLayoutInfo) {
            if (!_areaDisplayNameCache.has(entry.idArea)) {
              _areaDisplayNameCache.set(
                entry.idArea,
                (await getAreaEntry(entry.idArea))?.name ?? ""
              );
            }
            entry.areaDisplayName = _areaDisplayNameCache.get(entry.idArea);
          }
          setAvailableLayoutEntries(res);
        });
      });
  };

  const handleUpdateArea = async (info: AreaInfo) => {
    const succ = await updateAreaEntry(info);
    if (succ) {
      showSuccessNotification(t("success-updated-area"));
      getData();
      setShowEditAreaView(undefined);
    } else {
      showErrorNotification(t("error-updated-area"));
    }
  };

  const handleCreateArea = async (info: CreateAreaInfo) => {
    const succ = await addAreaEntry(info);
    if (succ) {
      showSuccessNotification(t("success-created-area"));
      getData();
      setShowCreateAreaView(false);
    } else {
      showErrorNotification(t("error-created-area"));
    }
  };

  const handleUpdateLayout = async (info: LayoutInfo) => {
    const succ = await updateLayoutEntry(info);
    if (succ) {
      showSuccessNotification(t("success-updated-layout"));
      getData();
      setShowEditLayoutView(undefined);
    } else {
      showErrorNotification(t("error-updated-layout"));
    }
  };

  const handleCreateLayout = async (info: CreateLayoutInfo) => {
    const succ = await addLayoutEntry(info);
    if (succ) {
      showSuccessNotification(t("success-created-layout"));
      getData();
      setShowCreateLayoutView(false);
    } else {
      showErrorNotification(t("error-created-layout"));
    }
  };

  const handleDeleteArea = async (idArea: number) => {
    const succ = await deleteAreaEntry(idArea);
    if (succ) {
      showSuccessNotification(t("success-deleted-area"));
      getData();
      setShowEditAreaView(undefined);
    } else {
      showErrorNotification(t("error-deleted-area"));
    }
  };

  const handleDeleteLayout = async (idLayout: number) => {
    const succ = await deleteLayyoutEntry(idLayout);
    if (succ) {
      showSuccessNotification(t("success-deleted-layout"));
      getData();
      setShowEditLayoutView(undefined);
    } else {
      showErrorNotification(t("error-deleted-layout"));
    }
  };

  return (
    <div className={`relative w-full h-full pb-2 flex flex-col justify-start items-center`}>
      <div className="w-11/12  min-h-10rem mt-7 bg-white flex flex-col justify-start items-center overflow-x-auto">
        <div className="w-11/12 mt-8">
          <h1 className="font-bold text-2xl text-jura-grey-font">{t("my-info")}</h1>
        </div>
        <div className="w-10/12 mt-3 flex flex-row justify-start items-center gap-14 text-jura-grey-font">
          <div className="flex flex-col">
            <span>{t("my-role")} </span>
            <span>{t("current-user")} </span>
          </div>
          <div className="flex flex-col">
            <span>{t(outletInfo.currentUser.role)}</span>
            <span>{`${outletInfo.currentUser.firstName} ${outletInfo.currentUser.surname}`}</span>
          </div>
        </div>

        {outletInfo.currentUser.role === OperatorRole.ADMIN && availableAreaEntries && (
          <div className="w-full max-h-customListHeight flex flex-col justify-start items-center ">
            <div className="w-11/12 mt-8">
              <h1 className="font-bold text-2xl text-jura-grey-font">{t("areas")}</h1>
            </div>
            <GeneralManagementList
              columns={[{ labelKey: "name", hasSorting: false }]}
              rowValues={availableAreaEntries}
              setAddRowView={setShowCreateAreaView}
              setEditRowView={(areaInfo) => {
                setShowEditAreaView(areaInfo as AreaInfo);
              }}
              selectBoxIdKey=""
            />
          </div>
        )}

        {availableLayoutEntries && (
          <div className="w-full  flex flex-col justify-start items-center ">
            <div className="w-11/12 mt-8">
              <h1 className="font-bold text-2xl text-jura-grey-font">{t("layouts")}</h1>
            </div>
            <GeneralManagementList
              columns={[
                { labelKey: "name", hasSorting: false },
                { labelKey: "idArea", hasSorting: false },
                { labelKey: "address", hasSorting: false },
                { labelKey: "label", hasSorting: false },
                { labelKey: "notice", hasSorting: false },
              ]}
              rowValues={availableLayoutEntries}
              setAddRowView={setShowCreateLayoutView}
              setEditRowView={(layoutInfo) => {
                setShowEditLayoutView(layoutInfo as LayoutInfo);
              }}
              selectBoxIdKey=""
            />
          </div>
        )}

        {availableOperators !== undefined && (
          <div className="w-full flex flex-col justify-start items-center ">
            <div className="w-11/12 mt-8">
              <h1 className="font-bold text-2xl text-jura-grey-font">{t("operators")}</h1>
            </div>
            <GeneralManagementList
              columns={[
                { labelKey: "role" },
                { labelKey: "idArea" },
                { labelKey: "surname", hasSorting: true },
                { labelKey: "firstName", hasSorting: true },
                { labelKey: "email" },
              ]}
              rowValues={availableOperators}
              setAddRowView={setShowInviteOperator}
              setEditRowView={(userInfo) => {
                setShowUpdateOperator(userInfo as OperatorInfo);
              }}
              selectBoxIdKey="email"
            />
          </div>
        )}
      </div>

      {showInviteOperator && (
        <InviteOperatorView
          setInviteOperatorView={setShowInviteOperator}
          currentOperatorUser={outletInfo.currentUser}
        />
      )}
      {showUpdateOperator !== undefined && (
        <EditOperatorView
          setEditOperatorView={setShowUpdateOperator}
          selectedOperator={showUpdateOperator}
        />
      )}

      {showEditAreaView && (
        <EditAreaEntry
          handleSaveEntry={(obj) => {
            handleUpdateArea(obj as AreaInfo);
          }}
          hideview={() => {
            setShowEditAreaView(undefined);
          }}
          selectedObjectEntry={showEditAreaView}
          handleDeleteEntry={() => handleDeleteArea(showEditAreaView.idArea)}
        />
      )}
      {showCreateAreaView && (
        <EditAreaEntry
          handleSaveEntry={(obj) => {
            handleCreateArea(obj as CreateAreaInfo);
          }}
          hideview={() => {
            setShowCreateAreaView(false);
          }}
        />
      )}
      {showEditLayoutView && (
        <EditLayoutEntry
          handleSaveEntry={(obj) => {
            handleUpdateLayout(obj as LayoutInfo);
          }}
          hideview={() => {
            setShowEditLayoutView(undefined);
          }}
          selectedObjectEntry={showEditLayoutView}
          handleDeleteEntry={() => handleDeleteLayout(showEditLayoutView.idLayout)}
        />
      )}
      {showCreateLayoutView && (
        <EditLayoutEntry
          handleSaveEntry={(obj) => {
            handleCreateLayout(obj as CreateLayoutInfo);
          }}
          hideview={() => {
            setShowCreateLayoutView(false);
          }}
        />
      )}
    </div>
  );
};

const EditAreaEntry = (props: {
  handleSaveEntry: (entryObj: CreateAreaInfo | AreaInfo) => void;
  hideview: () => void;
  selectedObjectEntry?: AreaInfo;
  handleDeleteEntry?: () => void;
}) => {
  const [t, i18n] = useTranslation();
  const [name, setName] = useState<string>(props.selectedObjectEntry?.name ?? "");
  const [disclaimerText, setDisclaimerText] = useState(
    props.selectedObjectEntry?.disclaimerText ?? ""
  );
  const [disclaimerTitle, setDisclaimerTitle] = useState(
    props.selectedObjectEntry?.disclaimerTitle ?? ""
  );
  // const [address, setAddress] = useState<string>(props.selectedObjectEntry?.address ?? "");

  const handleSave = () => {
    if (!name) {
      showErrorNotification(t("error-missing-values"));
      return;
    }
    const values = {
      name: name,
      idArea: props.selectedObjectEntry?.idArea,
      disclaimerText: disclaimerText,
      disclaimerTitle: disclaimerTitle,
    };
    props.handleSaveEntry(values);
  };

  const handleDelete = () => {
    props.handleDeleteEntry?.();
  };

  return (
    <div
      id="edit_area_overlay"
      className={`absolute w-full h-full top-0 bg-dark-transparent flex flex-col justify-center items-center`}
    >
      <div className="w-editAreaOverlay h-fit bg-jura-beige flex flex-col justify-start items-start">
        <div className="flex flex-row justify-between items-center w-full mt-2 ">
          <h2 className="font-semibold text-lg text-jura-grey-font ml-5">{t("edit-area-title")}</h2>
          <img
            src={closeIcon}
            className="w-5 cursor-pointer mr-1"
            onClick={() => {
              props.hideview();
            }}
          />
        </div>

        <div className="w-full mt-5 flex flex-row justify-start">
          {/* the area name */}
          <div className="ml-5 flex flex-col">
            <label htmlFor="areaEntry" className="text-sm font-semibold mb-1">
              {t("name")}
            </label>
            <input
              id="areaEntry"
              type="text"
              className="rounded-md min-w-15em p-1"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />

            <label htmlFor="disclaimerText" className="text-sm font-semibold mb-1 mt-5">
              {t("disclaimerNotice")}
            </label>
            <input
              id="disclaimerText"
              type="text"
              className="rounded-md min-w-15em p-1"
              value={disclaimerText}
              onChange={(e) => {
                setDisclaimerText(e.target.value);
              }}
            />
          </div>
          <div className="ml-5 flex flex-col">
            <label htmlFor="disclaimerTitle" className="text-sm font-semibold mb-1">
              {t("disclaimerTitle")}
            </label>
            <input
              id="disclaimerTitle"
              type="text"
              className="rounded-md min-w-15em p-1"
              value={disclaimerTitle}
              onChange={(e) => {
                setDisclaimerTitle(e.target.value);
              }}
            />
          </div>

          {/* the address */}
          {/* <div className="ml-5 flex flex-col">
            <label htmlFor="areaAddress" className="text-sm font-semibold mb-1">
              {t("address")}
            </label>
            <input
              id="areaAddress"
              type="text"
              className="rounded-md min-w-15em p-1"
              value={address}
              onChange={(e) => {
                setAddress(e.target.value);
              }}
            />
          </div> */}
        </div>

        <div className="w-full mt-8 mb-5 flex flex-row justify-center gap-5">
          <button
            className="bg-jura-darkgrey-button h-fit pl-4 pr-4 rounded-md text-white"
            onClick={() => {
              handleSave();
            }}
          >
            {t("save")}
          </button>
          {props.selectedObjectEntry && (
            <button
              className="bg-jura-darkgrey-button h-fit pl-4 pr-4 rounded-md text-white"
              onClick={() => {
                handleDelete();
              }}
            >
              {t("delete")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
const EditLayoutEntry = (props: {
  handleSaveEntry: (entryObj: CreateLayoutInfo | LayoutInfo) => void;
  hideview: () => void;
  selectedObjectEntry?: LayoutInfo;
  handleDeleteEntry?: () => void;
}) => {
  const [t, i18n] = useTranslation();
  const outletInfo = useOutletContext<{ currentUser: OperatorInfo }>();
  const [idArea, setIdArea] = useState<number>(
    props.selectedObjectEntry?.idArea ?? outletInfo.currentUser.idArea
  );
  const [availableAreas, setAvailableAreas] = useState<AreaInfo[]>([]);
  const [name, setName] = useState<string>(props.selectedObjectEntry?.name ?? "");
  const [label, setLabel] = useState<string>(props.selectedObjectEntry?.label ?? "");
  const [address, setAddress] = useState<string>(props.selectedObjectEntry?.address ?? "");
  const [notice, setNotice] = useState<string>(props.selectedObjectEntry?.notice ?? "");
  const [address1Labels, setAdress1Labels] = useState<string>(
    props.selectedObjectEntry?.address_1_labels ?? ""
  );
  const [type, setType] = useState<LayoutType>(
    (props.selectedObjectEntry?.type as LayoutType) ?? LayoutType.EMPLOYEE_CARD
  );
  const [website, setWebsite] = useState<string>(props.selectedObjectEntry?.website ?? "");
  const [companyName, setCompanyName] = useState<string>(
    props.selectedObjectEntry?.customCompanyName ?? ""
  );
  const [overrideDisclaimerTitle, setOverrideDisclaimerTitle] = useState<string>(
    props.selectedObjectEntry?.overrideDisclaimerTitle ?? ""
  );
  const [overrideDisclaimerNotice, setOverrideDisclaimerNotice] = useState<string>(
    props.selectedObjectEntry?.overrideDisclaimerText ?? ""
  );

  useEffect(() => {
    if (outletInfo.currentUser.role === OperatorRole.ADMIN) {
      getAllAreaEntries().then((res) => {
        setAvailableAreas(res);
      });
    } else {
      getAreaEntry(outletInfo.currentUser.idArea).then((res) => {
        if (res) setAvailableAreas([res]);
      });
    }
  }, []);

  const handleSave = () => {
    if (!name || !type) {
      showErrorNotification(t("error-missing-values"));
      return;
    }
    const values: CreateLayoutInfo | LayoutInfo = {
      name: name,
      label: label,
      idArea: idArea,
      address: address,
      notice: notice,
      website: website,
      type: type,
      idLayout: props.selectedObjectEntry?.idLayout,
      address_1_labels: address1Labels,
      customCompanyName: companyName,
      overrideDisclaimerTitle,
      overrideDisclaimerText: overrideDisclaimerNotice,
    };
    props.handleSaveEntry(values);
  };

  const handleDelete = () => {
    props.handleDeleteEntry?.();
  };

  return (
    <div
      id="edit_area_overlay"
      className={`absolute w-full h-full top-0 bg-dark-transparent flex flex-col justify-center items-center`}
    >
      <div className="w-editAreaOverlay h-fit bg-jura-beige flex flex-col justify-start items-start">
        <div className="flex flex-row justify-between items-center w-full mt-2 ">
          <h2 className="font-semibold text-lg text-jura-grey-font ml-5">
            {t("edit-layout-title")}
          </h2>
          <img
            src={closeIcon}
            className="w-5 cursor-pointer mr-1"
            onClick={() => {
              props.hideview();
            }}
          />
        </div>

        <div className="w-full mt-5 flex flex-row justify-start pl-5 gap-5">
          {/* the type */}
          <CardDropdownInput
            _id="layout_type_dropwdown"
            translationKey="type"
            value={t(type)}
            valueChangeHandler={(val) => {
              console.log("value changed for layout type:", val);
              setType(Object.values(LayoutType).find((e) => t(e) === val)!);
            }}
            optionValues={Object.values(LayoutType).map((val) => t(val))}
            customWidthClass="min-w-15em"
          />
          {/* the Area */}
          <CardDropdownInput
            _id="layout_area_dropwdown"
            translationKey="area"
            value={availableAreas.find((e) => e.idArea === idArea)?.name ?? availableAreas[0]?.name}
            valueChangeHandler={(val) => {
              console.log("value cahnged for area:", val);
              setIdArea(availableAreas.find((e) => e.name === val)?.idArea ?? -1);
            }}
            optionValues={availableAreas.map((val) => val.name)}
            customWidthClass="min-w-15em"
          />
        </div>
        <div className="w-full mt-5 flex flex-row justify-start">
          {/* the layout name */}
          <div className="ml-5 flex flex-col">
            <label htmlFor="layoutNameEntry" className="text-sm font-semibold mb-1">
              {t("name")}
            </label>
            <input
              id="layoutNameEntry"
              type="text"
              className="rounded-md min-w-15em p-1"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
          </div>

          {/* the layout label */}
          {type === LayoutType.EMPLOYEE_CARD && (
            <div className="ml-5 flex flex-col">
              <label htmlFor="layoutLabelEntry" className="text-sm font-semibold mb-1">
                {t("label")}
              </label>
              <input
                id="layoutLabelEntry"
                type="text"
                className="rounded-md min-w-15em p-1"
                value={label}
                onChange={(e) => {
                  setLabel(e.target.value);
                }}
              />
            </div>
          )}
        </div>

        {type === LayoutType.EMPLOYEE_CARD && (
          <div className="w-full mt-5 flex flex-row justify-start gap-5">
            {/* The layout notice */}
            <div className="ml-5 flex flex-col">
              <label htmlFor="layoutNoticeEntry" className="text-sm font-semibold mb-1">
                {t("notice")}
              </label>
              <input
                id="layoutNoticeEntry"
                type="text"
                className="rounded-md min-w-15em p-1"
                value={notice}
                onChange={(e) => {
                  setNotice(e.target.value);
                }}
              />
            </div>
          </div>
        )}

        {type === LayoutType.BASIC_CARD ||
          (type === LayoutType.DE_V3 && (
            <div className="w-full mt-5 flex flex-row justify-start gap-5">
              <div className="ml-5 flex flex-col">
                <label htmlFor="layoutWebsiteEntry" className="text-sm font-semibold mb-1">
                  {t("website")}
                </label>
                <input
                  id="layoutWebsiteEntry"
                  type="text"
                  className="rounded-md min-w-15em p-1"
                  value={website}
                  onChange={(e) => {
                    setWebsite(e.target.value);
                  }}
                />
              </div>
              <div className="flex flex-col">
                <label htmlFor="layoutCompanyNameEntry" className="text-sm font-semibold mb-1">
                  {t("company-name")}
                </label>
                <input
                  id="layoutCompanyNameEntry"
                  type="text"
                  className="rounded-md min-w-15em p-1"
                  value={companyName}
                  onChange={(e) => {
                    setCompanyName(e.target.value);
                  }}
                />
              </div>
            </div>
          ))}
        {type === LayoutType.DE_V3 && (
          <div className="w-full mt-5 flex flex-row justify-start gap-5">
            <div className="ml-5 flex flex-col">
              <label htmlFor="overrideDisclaimerTitle" className="text-sm font-semibold mb-1">
                {t("overrideDisclaimerTitle")}
              </label>
              <input
                id="overrideDisclaimerTitle"
                type="text"
                className="rounded-md min-w-15em p-1"
                value={overrideDisclaimerTitle}
                onChange={(e) => {
                  setOverrideDisclaimerTitle(e.target.value);
                }}
              />
            </div>
            <div className="ml flex flex-col">
              <label htmlFor="overrideDisclaimerNotice" className="text-sm font-semibold mb-1">
                {t("overrideDisclaimerNotice")}
              </label>
              <input
                id="overrideDisclaimerNotice"
                type="text"
                className="rounded-md min-w-15em p-1"
                value={overrideDisclaimerNotice}
                onChange={(e) => {
                  setOverrideDisclaimerNotice(e.target.value);
                }}
              />
            </div>
          </div>
        )}

        <div className="w-full mt-5 flex flex-row justify-start gap-5">
          {/* the address */}
          {type === LayoutType.EMPLOYEE_CARD ||
          type === LayoutType.BASIC_CARD ||
          type === LayoutType.DE_V3 ? (
            <div className="ml-5 flex flex-col">
              <label htmlFor="areaAddress" className="text-sm font-semibold mb-1">
                {t("address")}
              </label>
              <input
                id="areaAddress"
                type="text"
                className="rounded-md min-w-15em p-1"
                value={address}
                onChange={(e) => {
                  setAddress(e.target.value);
                }}
              />
            </div>
          ) : (
            <div></div>
          )}

          {/* The address 1 labels */}
          {type === LayoutType.DE_V3 && (
            <div className="flex flex-col">
              <label htmlFor="address_1_label_operator" className="text-sm font-semibold mb-1">
                {t("address_1_label_operator")}
              </label>
              <input
                id="address_1_label_operator"
                type="text"
                className="rounded-md min-w-15em p-1"
                value={address1Labels}
                onChange={(e) => {
                  setAdress1Labels(e.target.value);
                }}
              />
            </div>
          )}
        </div>

        <div className="w-full mt-8 mb-5 flex flex-row justify-center gap-5">
          <button
            className="bg-jura-darkgrey-button h-fit pl-4 pr-4 rounded-md text-white"
            onClick={() => {
              handleSave();
            }}
          >
            {t("save")}
          </button>
          {props.selectedObjectEntry && (
            <button
              className="bg-jura-darkgrey-button h-fit pl-4 pr-4 rounded-md text-white"
              onClick={() => {
                handleDelete();
              }}
            >
              {t("delete")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const InviteOperatorView = (props: {
  setInviteOperatorView: (show: boolean) => void;
  currentOperatorUser: OperatorInfo;
}) => {
  const [t, i18n] = useTranslation();

  const [inputEmail, setInputEmail] = useState("");
  const [availableAreas, setAvailableAreas] = useState<AreaInfo[]>([]);
  const [inputIdArea, setInputIdArea] = useState(props.currentOperatorUser.idArea); // set default to the user's area
  const [inputRole, setInputRole] = useState(OperatorRole.MANAGER);

  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (props.currentOperatorUser.role === OperatorRole.ADMIN) {
      getAllAreaEntries().then((res) => {
        if (res) setAvailableAreas(res);
      });
    } else {
      getAreaEntry(props.currentOperatorUser.idArea).then((res) => {
        if (res) setAvailableAreas([res]);
      });
    }
  }, []);

  const sendInvitation = () => {
    if (busy) {
      console.error("is busy");
      return;
    }
    if (!inputEmail || !inputIdArea || !inputRole || !inputEmail.includes("@")) {
      console.error("missing values from input form", inputEmail, inputIdArea, inputRole);
      showErrorNotification(t("error-missing-values"));
      return;
    }
    setBusy(true);
    sendOperatorInvitation(inputEmail, inputRole, inputIdArea).then((succ) => {
      if (succ) {
        props.setInviteOperatorView(false);
        showSuccessNotification(t("success-operator-invite"));
      } else {
        showErrorNotification(t("error-operator-invite"));
      }

      setBusy(false);
    });
  };

  return (
    <div
      id="invite_operator_overlay"
      className={`absolute w-full h-full top-0 bg-dark-transparent flex flex-col justify-center items-center`}
    >
      <div className="w-30em h-fit bg-jura-beige flex flex-col justify-start items-start">
        <div className="flex flex-row justify-between items-center w-full mt-2 ">
          <h2 className="font-semibold text-lg text-jura-grey-font ml-5">{t("inviteUser")}</h2>
          <img
            src={closeIcon}
            className="w-5 cursor-pointer mr-1"
            onClick={() => {
              props.setInviteOperatorView(false);
            }}
          />
        </div>

        {/* The Area and Role input */}
        <div className="mt-4 ml-5 flex flex-row w-fit items-center gap-5">
          {availableAreas !== undefined && (
            <CardDropdownInput
              _id="area_input"
              translationKey="area"
              value={
                availableAreas.find((e) => e.idArea === inputIdArea)?.name ??
                availableAreas[0]?.name
              }
              valueChangeHandler={(val) =>
                setInputIdArea(availableAreas.find((e) => e.name === val)?.idArea ?? -1)
              }
              // optionValues={availableAreas.map((val) => val.idArea)}
              optionValues={availableAreas.map((val) => val.name)}
            />
          )}
          <div className="flex flex-col ml-5">
            <label htmlFor="role_input" className="mb-1 text-sm font-semibold">
              {t("role")}
            </label>
            {/* <input id="role_input" type="text" className="rounded-md" placeholder="area" /> */}
            <select
              id="role_input"
              name="roles"
              className="rounded-md p-1"
              defaultValue={OperatorRole.MANAGER}
              onChange={(e) => {
                setInputRole(e.target.value as OperatorRole);
              }}
            >
              <option value={OperatorRole.MANAGER}>{t(OperatorRole.MANAGER)} </option>
              {props.currentOperatorUser.role === OperatorRole.ADMIN && (
                <option value={OperatorRole.ADMIN}>{t(OperatorRole.ADMIN)}</option>
              )}
            </select>
          </div>
        </div>

        {/* Email input */}
        <div className="mt-2 ml-5 pb-5 flex flex-col">
          <label htmlFor="email_input" className="mb-1 text-sm font-semibold">
            {t("email")}
          </label>
          <input
            id="email_input"
            type="text"
            className="rounded-md min-w-15em p-1"
            placeholder={t("exampleEmail")}
            onChange={(e) => {
              setInputEmail(e.target.value);
            }}
          ></input>
        </div>

        <div className="flex flex-row w-full mb-5 justify-center">
          <button
            className="bg-jura-grey-button p-1 rounded-md min-w-fit"
            onClick={() => sendInvitation()}
          >
            {t("send")}
          </button>
        </div>
      </div>
    </div>
  );
};

const EditOperatorView = (props: {
  setEditOperatorView: (selectedOperator: OperatorInfo | undefined) => void;
  selectedOperator: OperatorInfo;
}) => {
  const [t, i18n] = useTranslation();

  const [availableAreas, setAvailableAreas] = useState<AreaInfo[]>([]);
  const [inputIdArea, setInputIdArea] = useState(props.selectedOperator.idArea);
  const [inputRole, setInputRole] = useState<OperatorRole>(props.selectedOperator.role);

  const [busy, setBusy] = useState(false);
  const outletInfo = useOutletContext<{ currentUser: OperatorInfo }>();

  useEffect(() => {
    if (outletInfo.currentUser.role === OperatorRole.ADMIN) {
      getAllAreaEntries().then((res) => {
        if (res) setAvailableAreas(res);
      });
    } else {
      getAreaEntry(outletInfo.currentUser.idArea).then((res) => {
        if (res) setAvailableAreas([res]);
      });
    }
  }, []);

  const sendUpdate = () => {
    if (!props.selectedOperator) {
      console.error("selected operator is undefinfed");
      return;
    }
    if (busy) {
      console.error("is busy");
      return;
    }
    if (!inputIdArea || !inputRole) {
      console.error("missing values from input form");
      showErrorNotification(t("error-missing-values"));
      return;
    }
    setBusy(true);
    updateOperatorValues({
      activated: props.selectedOperator.activated,
      email: props.selectedOperator.email,
      firstName: props.selectedOperator.firstName,
      role: inputRole,
      surname: props.selectedOperator.surname,
      idArea: inputIdArea,
    }).then((succ) => {
      if (succ) {
        props.setEditOperatorView(undefined);
        showSuccessNotification(t("success-operator-update"));
      } else {
        showErrorNotification(t("error-operator-update"));
      }
      setBusy(false);
    });
  };

  return (
    <div
      id="invite_operator_overlay"
      className={`absolute w-full h-full top-0 bg-dark-transparent flex flex-col justify-center items-center`}
    >
      <div className="w-30em h-fit bg-jura-beige flex flex-col justify-start items-start">
        <div className="flex flex-row justify-between items-center w-full mt-2 ">
          <h2 className="font-semibold text-lg text-jura-grey-font ml-5">{t("updateOperator")}</h2>
          <img
            src={closeIcon}
            className="w-5 cursor-pointer mr-1"
            onClick={() => {
              props.setEditOperatorView(undefined);
            }}
          />
        </div>

        {/* The Area and Role input */}
        {outletInfo.currentUser.role === OperatorRole.ADMIN && (
          <div className="mt-4 ml-5 flex flex-row w-fit items-center gap-5">
            {availableAreas !== undefined && (
              <CardDropdownInput
                _id="area_input"
                translationKey="area"
                value={
                  availableAreas.find((e) => e.idArea === inputIdArea)?.name ??
                  availableAreas[0]?.name
                }
                valueChangeHandler={(val) =>
                  setInputIdArea(availableAreas.find((e) => e.name === val)?.idArea ?? inputIdArea)
                }
                // optionValues={availableAreas.map((val) => val.idArea)}
                optionValues={availableAreas.map((val) => val.name)}
              />
            )}
            <div className="flex flex-col">
              <label htmlFor="role_input" className="mb-1 text-sm font-semibold">
                {t("role")}
              </label>

              <select
                id="role_input"
                name="roles"
                className="rounded-md p-1"
                defaultValue={props.selectedOperator.role}
                onChange={(e) => {
                  setInputRole(e.target.value as OperatorRole);
                }}
              >
                <option value={OperatorRole.MANAGER}>{t(OperatorRole.MANAGER)} </option>
                <option value={OperatorRole.ADMIN}>{t(OperatorRole.ADMIN)}</option>
              </select>
            </div>
          </div>
        )}

        <div className="flex flex-row w-full mb-5 mt-5 justify-center gap-4">
          {outletInfo.currentUser.role === OperatorRole.ADMIN && (
            <button
              className="bg-jura-grey-button p-1  pl-2 pr-2 rounded-md min-w-fit"
              onClick={() => sendUpdate()}
            >
              {t("send")}
            </button>
          )}

          <button
            className="bg-jura-grey-button p-1 pl-2 pr-2 rounded-md min-w-fit"
            onClick={() => {
              deleteOperator(props.selectedOperator?.email).then((res) => {
                if (res) window.location.reload();
              });
            }}
          >
            {t("delete")}
          </button>
        </div>
      </div>
    </div>
  );
};
